/**
 * Estoque (drill-down de "estoque crítico").
 *
 * Exibe SEMPRE a disponibilidade consolidada (`consolidatedAvailability` de
 * `packages/catalog`), nunca estoque bruto (D-11.5). Desde a Task 11 as
 * reservas ativas vêm do catálogo em duas camadas e são SUBTRAÍDAS aqui com a
 * mesma conta do KPI; quando a fonte de reservas não está publicada no
 * ambiente, a nota diz isso em vez de fingir precisão.
 *
 * Migração para `.tsx` (Cluster B — Catálogo & Estoque, redesign por perfil):
 * mesmo markup/contrato de antes, só a sintaxe `createElement` → JSX. Import
 * de `React` explícito — ver nota equivalente em `layout/AppShell.tsx`.
 */
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RecordTable } from "../components/RecordTable.js";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { DrillDownHeader } from "../components/DrillDownHeader.js";
import { catalogEntriesForKpi, parseKpiId } from "../dashboard/drilldown.js";
import {
  availabilityOf,
  CRITICAL_STOCK_THRESHOLD,
  isSourceAwaitingContract,
  reservationsOf,
  type DashboardDataset
} from "../dashboard/kpis.js";
import { formatBrl, formatCount } from "../format.js";
import type { PlatformCatalogEntry } from "../api/types.js";
import type { DrillDownViewProps } from "../dashboard/view-props.js";

export function EstoqueView({ dataset, kpiId, now }: DrillDownViewProps): ReactElement {
  const entries = catalogEntriesForKpi(dataset, kpiId, now ?? new Date());
  // Reservas da MESMA fonte que o KPI usou (catálogo em duas camadas, Task 11).
  const reservations = reservationsOf(dataset);
  const reservationsPending = isSourceAwaitingContract(dataset, "platformCatalog");
  return (
    <div className="page page--estoque flex flex-col gap-6" data-page="estoque">
      <DrillDownHeader title="Disponibilidade de estoque" kpiId={kpiId} recordCount={entries.length} recordNoun="item(ns)" />
      <p className="page__note text-sm text-muted">
        {`Corte de criticidade: disponibilidade consolidada <= ${CRITICAL_STOCK_THRESHOLD}. ` +
          (reservationsPending
            ? "Fonte de reservas (catálogo em duas camadas) não publicada neste ambiente: a disponibilidade está sem desconto de reserva."
            : "Disponibilidade = estoque vendável − reservas ativas.")}
      </p>
      <p className="page__note flex flex-wrap items-center gap-2 text-sm text-muted">
        <Link to="/disponibilidade" className="font-semibold text-brand-text hover:underline">
          Ver disponibilidade e reservas item a item
        </Link>
        <span aria-hidden>·</span>
        <Link to="/divergencias" className="font-semibold text-brand-text hover:underline">
          Ver divergências Bling↔local
        </Link>
      </p>
      <RecordTable<PlatformCatalogEntry>
        caption="Itens de catálogo e disponibilidade consolidada"
        rows={entries}
        idOf={(entry) => entry.product.id}
        emptyMessage="Nenhum item em estoque crítico."
        // Fase 7 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
        animateRows
        columns={[
          { key: "sku", label: "SKU", render: (entry) => entry.product.sku },
          { key: "name", label: "Produto", render: (entry) => entry.product.name },
          {
            key: "availability",
            label: "Disponibilidade consolidada",
            render: (entry) => {
              const availability = availabilityOf(entry, reservations.get(entry.product.id) ?? 0);
              return availability === null ? "sem oferta vigente" : formatCount(availability);
            }
          },
          {
            key: "reserved",
            label: "Reservas ativas",
            render: (entry) => (reservationsPending ? "fonte não publicada" : formatCount(reservations.get(entry.product.id) ?? 0))
          },
          {
            key: "price",
            label: "Preço vigente",
            render: (entry) => (entry.offer ? formatBrl(entry.offer.priceCents) : "—")
          }
        ]}
      />
    </div>
  );
}

export function Estoque(): ReactElement {
  const [searchParams] = useSearchParams();
  const kpiId = parseKpiId(searchParams.get("kpi"));
  return <DatasetBoundary>{(dataset: DashboardDataset) => <EstoqueView dataset={dataset} kpiId={kpiId} />}</DatasetBoundary>;
}
